import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import "@/App.css";

import { AuthProvider, useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import AriesWidget from "@/components/AriesWidget";

import Home from "@/pages/Home";
import Servizi from "@/pages/Servizi";
import ServizioDetail from "@/pages/ServizioDetail";
import Academy from "@/pages/Academy";
import CorsoDetail from "@/pages/CorsoDetail";
import Eventi from "@/pages/Eventi";
import Blog from "@/pages/Blog";
import ArticoloDetail from "@/pages/ArticoloDetail";
import Fondatore from "@/pages/Fondatore";
import Contatti from "@/pages/Contatti";
import Accedi from "@/pages/Accedi";
import AreaStudente from "@/pages/AreaStudente";
import Admin from "@/pages/Admin";
import LegalPage from "@/pages/LegalPage";
import ResetPassword from "@/pages/ResetPassword";
import TemaNatale from "@/pages/TemaNatale";
import Quiz from "@/pages/Quiz";
import SoundtrackPlayer from "@/components/SoundtrackPlayer";
import CookieBanner from "@/components/CookieBanner";
import Seo from "@/components/Seo";
import { track } from "@/lib/analytics";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function AnalyticsTracker() {
  const { pathname } = useLocation();
  useEffect(() => { track("pageview", pathname); }, [pathname]);
  return null;
}

function SmoothScroll() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);
  return null;
}

function ProtectedRoute({ children, adminOnly = false }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen flex items-center justify-center text-gold font-display text-xl">Lux Aeterna…</div>;
  if (!user) return <Navigate to="/accedi" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/area-studente" replace />;
  return children;
}

function Layout() {
  const { pathname } = useLocation();
  const bare = pathname === "/accedi" || pathname === "/reset-password";
  return (
    <>
      {!bare && <Navbar />}
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/servizi" element={<Servizi />} />
          <Route path="/servizi/:slug" element={<ServizioDetail />} />
          <Route path="/academy" element={<Academy />} />
          <Route path="/academy/:slug" element={<CorsoDetail />} />
          <Route path="/eventi" element={<Eventi />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<ArticoloDetail />} />
          <Route path="/fondatore" element={<Fondatore />} />
          <Route path="/contatti" element={<Contatti />} />
          <Route path="/accedi" element={<><Seo title="Accedi" noindex /><Accedi /></>} />
          <Route path="/reset-password" element={<><Seo title="Reimposta password" noindex /><ResetPassword /></>} />
          <Route path="/legal/:slug" element={<LegalPage />} />
          <Route path="/tema-natale" element={<TemaNatale />} />
          <Route path="/quiz" element={<Quiz />} />
          <Route path="/area-studente" element={<ProtectedRoute><Seo title="Area Studente" noindex /><AreaStudente /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute adminOnly><Seo title="Dashboard" noindex /><Admin /></ProtectedRoute>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      {!bare && <Footer />}
      {!bare && <AriesWidget />}
      {!bare && <CookieBanner />}
      {!bare && <SoundtrackPlayer visible={pathname === "/tema-natale" || pathname === "/quiz"} />}
    </>
  );
}

function App() {
  return (
    <div className="App grain min-h-screen bg-[#F7EFE0]">
      <AuthProvider>
        <BrowserRouter>
          <SmoothScroll />
          <ScrollToTop />
          <AnalyticsTracker />
          <Layout />
          <Toaster position="bottom-right" theme="light" toastOptions={{ style: { background: "#FAF3E4", border: "1px solid rgba(185,134,63,0.35)", color: "#2A1F14" } }} />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
